import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import { Button } from "../components/ui/button";
import { playGameSample, unlockAudioContext } from "../lib/playAudio";
import { PlayCircle, Volume2, Mic, Square } from "lucide-react";

const SOUND_TEST_SRC = `${process.env.PUBLIC_URL || ""}/audio/alarme.mp3`;
const MIC_MAX_RECORD_MS = 8000;

function getAudioRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

const Welcome = () => {
  const navigate = useNavigate();
  const { token } = useGame();
  const [soundTesting, setSoundTesting] = useState(false);
  const [micRecording, setMicRecording] = useState(false);
  const [micPlaybackUrl, setMicPlaybackUrl] = useState(null);
  const [micPlaying, setMicPlaying] = useState(false);
  const [micError, setMicError] = useState("");
  const micRecorderRef = useRef(null);
  const micChunksRef = useRef([]);
  const micStreamRef = useRef(null);
  const micStopTimerRef = useRef(null);
  const micPlaybackRef = useRef(null);
  const micPlaybackUrlRef = useRef(null);

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  const clearMicPlayback = useCallback(() => {
    if (micPlaybackRef.current) {
      micPlaybackRef.current.pause();
      micPlaybackRef.current = null;
    }
    if (micPlaybackUrlRef.current) {
      URL.revokeObjectURL(micPlaybackUrlRef.current);
      micPlaybackUrlRef.current = null;
    }
    setMicPlaybackUrl(null);
    setMicPlaying(false);
  }, []);

  const stopMicResources = useCallback(() => {
    if (micStopTimerRef.current) {
      clearTimeout(micStopTimerRef.current);
      micStopTimerRef.current = null;
    }
    if (micRecorderRef.current && micRecorderRef.current.state !== "inactive") {
      micRecorderRef.current.stop();
    }
    micRecorderRef.current = null;
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    setMicRecording(false);
  }, []);

  useEffect(
    () => () => {
      stopMicResources();
      clearMicPlayback();
    },
    [stopMicResources, clearMicPlayback]
  );

  const testSound = async () => {
    setSoundTesting(true);
    try {
      await unlockAudioContext();
      await playGameSample(SOUND_TEST_SRC, 1);
    } finally {
      setSoundTesting(false);
    }
  };

  const stopMicRecording = useCallback(() => {
    if (micStopTimerRef.current) {
      clearTimeout(micStopTimerRef.current);
      micStopTimerRef.current = null;
    }
    if (micRecorderRef.current && micRecorderRef.current.state === "recording") {
      micRecorderRef.current.stop();
      return;
    }
    stopMicResources();
  }, [stopMicResources]);

  const startMicRecording = async () => {
    setMicError("");
    clearMicPlayback();
    stopMicResources();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      micChunksRef.current = [];
      const mimeType = getAudioRecorderMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      micRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) micChunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(micChunksRef.current, {
          type: recorder.mimeType || mimeType || "audio/webm",
        });
        micChunksRef.current = [];
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          micPlaybackUrlRef.current = url;
          setMicPlaybackUrl(url);
        } else {
          setMicError("Aucun son enregistré. Réessayez en parlant plus fort.");
        }
        if (micStreamRef.current) {
          micStreamRef.current.getTracks().forEach((track) => track.stop());
          micStreamRef.current = null;
        }
        micRecorderRef.current = null;
        setMicRecording(false);
      };

      recorder.start();
      setMicRecording(true);
      micStopTimerRef.current = setTimeout(() => stopMicRecording(), MIC_MAX_RECORD_MS);
    } catch {
      setMicError("Micro inaccessible. Autorisez le micro dans votre navigateur.");
      stopMicResources();
    }
  };

  const playMicPlayback = async () => {
    if (!micPlaybackUrl) return;
    setMicError("");
    setMicPlaying(true);
    try {
      if (micPlaybackRef.current) {
        micPlaybackRef.current.pause();
      }
      const audio = new Audio(micPlaybackUrl);
      micPlaybackRef.current = audio;
      audio.onended = () => setMicPlaying(false);
      audio.onerror = () => {
        setMicPlaying(false);
        setMicError("Impossible de lire l'enregistrement.");
      };
      await audio.play();
    } catch {
      setMicPlaying(false);
      setMicError("Impossible de lire l'enregistrement.");
    }
  };

  return (
    <PageLayout wide>
      <div className="text-center fade-up max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">
          Vérifiez votre <span className="text-md-cyan">matériel</span>
        </h2>
        <p className="text-white/55 text-sm sm:text-base leading-relaxed">
          Avant de commencer, testez le son de votre ordinateur et votre microphone.
        </p>
      </div>

      <section className="mt-8 max-w-4xl mx-auto fade-up" data-testid="welcome-equipment">
        <div className="md-card overflow-hidden">
          <div className="grid sm:grid-cols-2 sm:divide-x sm:divide-white/10">
            <div className="p-5 border-b sm:border-b-0 border-white/10 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Volume2 className="text-md-cyan shrink-0" size={18} />
                <h4 className="font-semibold text-white text-sm">Son de l&apos;ordinateur</h4>
              </div>
              <p className="text-sm text-white/55 leading-snug mb-4">
                Mettez le volume de votre ordinateur à environ <strong className="text-white/80">50 %</strong>, puis
                testez les effets sonores.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void testSound()}
                disabled={soundTesting}
                className="shrink-0 border-md-cyan/30 bg-md-cyan/10 text-md-cyan hover:bg-md-cyan/20 hover:text-white"
                data-testid="welcome-sound-test"
              >
                <Volume2 size={14} className="mr-1.5" />
                {soundTesting ? "Lecture…" : "Tester le son"}
              </Button>
              <p className="text-sm text-white/55 leading-snug mt-3">
                Si vous entendez l&apos;alarme de départ du chronomètre, le son fonctionne correctement sur votre
                appareil.
              </p>
            </div>

            <div className="p-5 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Mic className="text-md-cyan shrink-0" size={18} />
                <h4 className="font-semibold text-white text-sm">Microphone</h4>
              </div>
              <p className="text-sm text-white/55 leading-snug mb-4">
                Enregistrez quelques secondes de votre voix, puis écoutez l&apos;enregistrement pour vérifier que votre
                micro fonctionne.
              </p>
              <div className="flex flex-col items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => (micRecording ? stopMicRecording() : void startMicRecording())}
                  disabled={micPlaying}
                  className="shrink-0 border-md-cyan/30 bg-md-cyan/10 text-md-cyan hover:bg-md-cyan/20 hover:text-white"
                  data-testid="welcome-mic-test"
                >
                  {micRecording ? (
                    <>
                      <Square size={14} className="mr-1.5 fill-current" />
                      Arrêter l&apos;enregistrement
                    </>
                  ) : (
                    <>
                      <Mic size={14} className="mr-1.5" />
                      {micPlaybackUrl ? "Réenregistrer" : "Enregistrer"}
                    </>
                  )}
                </Button>
                {micPlaybackUrl && !micRecording ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void playMicPlayback()}
                    disabled={micPlaying}
                    className="shrink-0 border-md-cyan/30 bg-md-cyan/10 text-md-cyan hover:bg-md-cyan/20 hover:text-white"
                    data-testid="welcome-mic-playback"
                  >
                    <Volume2 size={14} className="mr-1.5" />
                    {micPlaying ? "Lecture…" : "Écouter l'enregistrement"}
                  </Button>
                ) : null}
              </div>
              <p className="text-sm text-white/55 leading-snug mt-3">
                Si vous entendez vos voix, le micro fonctionne correctement.
              </p>
              <div
                className="md-card mt-4 p-4 text-center border border-amber-400/25 bg-amber-400/5"
                data-testid="welcome-mic-fan-tip"
              >
                <div className="text-sm leading-relaxed">
                  <p className="font-semibold text-amber-200 mb-1">Attention au micro</p>
                  <p className="text-white/80">
                    <strong className="text-white font-bold">
                      Si vous avez un ventilateur ou une climatisation, ne l&apos;orientez pas vers
                      l&apos;ordinateur
                    </strong>
                    . Le souffle peut saturer le micro et couvrir tous les autres sons.
                  </p>
                </div>
              </div>
              {micRecording ? (
                <p className="text-xs text-md-cyan mt-3">Enregistrement en cours… Parlez maintenant.</p>
              ) : null}
              {micError ? <p className="text-xs text-amber-300 mt-3">{micError}</p> : null}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-md mx-auto">
        <MdGlowButton
          onClick={() => navigate("/comment-jouer")}
          wrapClassName="mt-8"
          data-testid="welcome-continue-button"
        >
          <PlayCircle className="w-5 h-5 shrink-0" />
          Continuer vers le tutoriel
        </MdGlowButton>
      </div>
    </PageLayout>
  );
};

export default Welcome;
